import { useState } from 'react';
import type { Product } from '@coreledger/shared-types';

const GST_RATES = [0, 0.1, 0.25, 1, 1.5, 3, 5, 6, 7.5, 12, 18, 28];

interface Props {
  show: boolean;
  onClose: () => void;
  products: Product[];
  onAddProduct: (p: Product) => void;
  onCreateProduct: (data: { name: string; hsnSac?: string; unit: string; rate: number; gstRate: number }) => Promise<Product>;
}

export function ProductPickerModal({ show, onClose, products, onAddProduct, onCreateProduct }: Props) {
  const [productSearch, setProductSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', hsnSac: '', unit: 'pcs', rate: 0, gstRate: 18 });
  const [saving, setSaving] = useState(false);

  if (!show) return null;

  const filtered = products.filter(p => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()));

  function handleClose() {
    setProductSearch('');
    setShowCreate(false);
    setForm({ name: '', hsnSac: '', unit: 'pcs', rate: 0, gstRate: 18 });
    onClose();
  }

  async function handleCreate() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const created = await onCreateProduct({ name: form.name.trim(), hsnSac: form.hsnSac || undefined, unit: form.unit, rate: form.rate, gstRate: form.gstRate });
      onAddProduct(created);
      setShowCreate(false);
      setForm({ name: '', hsnSac: '', unit: 'pcs', rate: 0, gstRate: 18 });
    } catch { alert('Failed to create product'); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">📦 Add Products</span>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>
        <div className="modal-body">
          <input autoFocus value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search products…" style={{ width: '100%', marginBottom: '0.75rem' }} />
          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            {filtered.map(p => (
              <div key={p.id}
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.55rem 0.5rem', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                onClick={() => onAddProduct(p)}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{p.name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{p.hsnSac ? `HSN: ${p.hsnSac} · ` : ''}{p.gstRate}% GST{p.stock != null ? ` · Stock: ${p.stock} ${p.unit ?? ''}` : ''}</div>
                </div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--primary)' }}>₹{(p.rate ?? 0).toFixed(2)}</div>
              </div>
            ))}
            {filtered.length === 0 && !showCreate && (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem', fontSize: '0.85rem' }}>No products found</div>
            )}
          </div>
          {showCreate && (
            <div style={{ borderTop: '2px solid var(--primary)', marginTop: '0.75rem', paddingTop: '0.75rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.6rem', color: 'var(--primary)' }}>New Product</div>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                <input autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Product name *" style={{ flex: '2 1 140px' }} />
                <input value={form.hsnSac} onChange={e => setForm(f => ({ ...f, hsnSac: e.target.value }))} placeholder="HSN/SAC" style={{ flex: '1 1 80px' }} />
                <input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="Unit" style={{ flex: '1 1 60px' }} />
                <input type="number" value={form.rate || ''} onChange={e => setForm(f => ({ ...f, rate: parseFloat(e.target.value) || 0 }))} placeholder="Rate ₹" style={{ flex: '1 1 80px' }} />
                <select value={form.gstRate} onChange={e => setForm(f => ({ ...f, gstRate: parseFloat(e.target.value) }))} style={{ flex: '1 1 80px' }}>
                  {GST_RATES.map(r => <option key={r} value={r}>{r}% GST</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setShowCreate(false); setForm({ name: '', hsnSac: '', unit: 'pcs', rate: 0, gstRate: 18 }); }}>Cancel</button>
                <button type="button" className="btn btn-primary btn-sm" disabled={saving || !form.name.trim()} onClick={handleCreate}>
                  {saving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : 'Save & Add'}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setShowCreate(c => !c); setForm(f => ({ ...f, name: productSearch })); }}>
            ＋ New Product
          </button>
          <button className="btn btn-ghost" onClick={handleClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
